import {
  clearCompletedMemberApplicationOperation,
  getOrCreateMemberApplicationOperation,
  MemberApplicationOperationStorage,
} from "../../Members/memberApplicationOperation";

export type ManageInvoiceActionOperationKind =
  | "EMAIL_DOCUMENT"
  | "ISSUE_CREDIT"
  | "UPDATE_DRAFT"
  | "VOID";

export const getManageInvoiceActionOperationStorageKey = ({
  action,
  chamberId,
  invoiceId,
}: {
  action: ManageInvoiceActionOperationKind;
  chamberId: string;
  invoiceId: string;
}) =>
  `cmp-manage-invoice-action:v1:${action}:${encodeURIComponent(
    chamberId,
  )}:${encodeURIComponent(invoiceId)}`;

export const runManageInvoiceActionOperation = async <
  MutationInput extends object,
  MutationResult,
>({
  action,
  chamberId,
  createOperationId,
  execute,
  fingerprintRequest,
  input,
  invoiceId,
  isAuthoritativelyCompleted,
  storage,
}: {
  action: ManageInvoiceActionOperationKind;
  chamberId: string;
  createOperationId?: () => string;
  execute: (
    input: Omit<MutationInput, "operationId"> & { operationId: string },
  ) => Promise<MutationResult>;
  fingerprintRequest?: (request: unknown) => Promise<string>;
  input: MutationInput & { operationId?: unknown };
  invoiceId: string;
  isAuthoritativelyCompleted: (result: MutationResult) => boolean;
  storage?: MemberApplicationOperationStorage;
}): Promise<MutationResult> => {
  const operationFreeInput = { ...input } as Record<string, unknown>;
  delete operationFreeInput.operationId;

  const storageKey = getManageInvoiceActionOperationStorageKey({
    action,
    chamberId,
    invoiceId,
  });
  const operation = await getOrCreateMemberApplicationOperation({
    ...(createOperationId ? { createOperationId } : {}),
    ...(fingerprintRequest ? { fingerprintRequest } : {}),
    request: {
      action,
      chamberId,
      input: operationFreeInput,
      invoiceId,
    },
    ...(storage ? { storage } : {}),
    storageKey,
  });
  const result = await execute({
    ...operationFreeInput,
    operationId: operation.operationId,
  } as Omit<MutationInput, "operationId"> & { operationId: string });

  if (isAuthoritativelyCompleted(result)) {
    clearCompletedMemberApplicationOperation({
      operationId: operation.operationId,
      ...(storage ? { storage } : {}),
      storageKey,
    });
  }

  return result;
};
